import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import {geoUtils} from "../utils/geo-utils";
import _ from "lodash";
import {createSelector} from "reselect";

// Simple map to create abbreviations related to fips id
const stateNameMap = [
  {id: 50, n: "Vermont", a: "VT"},
  {id: 33, n: "New Hampshire", a: "NH"},
  {id: 25, n: "Massachusetts", a: "MA"},
  {id: 44, n: "Rhode Island", a: "RI"},
  {id: 9, n: "Connecticut", a: "CT"},
  {id: 34, n: "New Jersey", a: "NJ"},
  {id: 10, n: "Delaware", a: "DE"},
  {id: 24, n: "Maryland", a: "MD"},
  {id: 11, n: "District of Columbia", a: "DC"}
];

/**
 * Component for drawing the small state callout boxes to the right of the States map.
 *
 * The callout boxes are defined by a static internal list limited to:
 * VT, NH, MA, RI, CT, NJ, DE, MD, DC
 *
 * These callout boxes are intended to give us a place large enough to hold a text abbreviation
 * for states that are too small for text labels or easy interaction
 *
 * The color of each box is determined by the data and the quartile of the state
 * using the geoUtils.createQuartileFillScale
 *
 * @property height - height of the parent svg in pixels for placement
 * @property width - width of the parent svg in pixels for placement
 * @property data - array of location models to calculate the legend values
 * @property dict - dictionary of data keyed by the state fips
 * @property dimension - field in data objects that holds the quartile value for classification
 * @property onClick - callback when a county is clicked (stateFips,cent)
 * @property onAltClick - callback when a county is alt clicked (stateFips,cent)
 * @property onHoverOver - callback when a county is moused over (stateFips,cent)
 * @property onHoverOut - callback when a county is moused out (stateFips)
 *
 * @example
 * return (
 *   <UsaStateCalloutBoxes
 *     width={500}
 *     height={500}
 *     data={[...]}
 *     dict={{...}}
 *     dimension={'quartile'}
 *     onClick={(stateFips, cent) => {}}
 *     onAltClick={(stateFips, cent) => {}}
 *     onHoverOver={(stateFips, cent) => {}}
 *     onHoverOut={(stateFips) => {}}
 *   />
 * )
 */
export default class UsaStateCalloutBoxes extends PureComponent {

  static propTypes = {
    width: PropTypes.number,
    height: PropTypes.number,
    data: PropTypes.array,
    dict: PropTypes.object,
    dimension: PropTypes.string,
    onHoverOver: PropTypes.func,
    onHoverOut: PropTypes.func,
    onClick: PropTypes.func,
    onAltClick: PropTypes.func
  }

  getWidth = (props) => props.width
  getHeight = (props) => props.height
  getData = (props) => props.data
  getDimension = (props) => props.dimension

  getPaths = createSelector(
    [this.getWidth, this.getHeight],
    (width, height) => geoUtils.getAlbersUsaStatePaths(width, height)
  )

  getScale = createSelector(
    [this.getData, this.getDimension],
    (data, dimension) => geoUtils.createQuartileFillScale(data, dimension)
  )

  getTextFill = createSelector(
    [this.getScale],
    (scale) => (value) => scale(value) === "#d7311f" ? "#ffffff" : "#000000"
  )

  getTextStroke = createSelector(
    [this.getScale],
    (scale) => (value) => scale(value) === "url(#crosshatch)"
  )

  render() {
    const paths = this.getPaths(this.props);
    const scale = this.getScale(this.props);
    const textFill = this.getTextFill(this.props);
    const shouldStrokeText = this.getTextStroke(this.props);

    const getValue = d => _.get(this.props.dict, [d.id], {});
    const getCenter = d => _.get(_.find(paths, ['id', d.id]), 'cent');

    const getHandlers = state => {
      const onClick = this.props.onClick;
      const onAltClick = this.props.onAltClick;
      const onHoverOver = this.props.onHoverOver;
      const onHoverOut = this.props.onHoverOut;
      const handlers = {};

      if (onClick) {
        handlers.onClick = (event) => {
          if (onAltClick && (event.metaKey || event.ctrlKey)) {
            onAltClick(state.id, state.cent);
          }
          else {
            onClick(state.id, state.cent);
          }
        }
      }

      if (onHoverOver) {
        handlers.onMouseEnter = () => onHoverOver(state.id, getCenter(state));

        if (onHoverOut) {
          handlers.onMouseOut = () => onHoverOut(state.id, getCenter(state));
        }
        else {
          handlers.onMouseOut = () => onHoverOver(null);
        }
      }

      return handlers;
    };

    const x = this.props.width - 25;
    const y = this.props.height * 0.5 - 115;

    return (
      <g className="UsaStateCalloutBoxes" transform={`translate(${x},${y})`}>
        {stateNameMap.map((d, i) => (
          <g key={d.id}
             transform={`translate(2,${i * 27})`}
             {...getHandlers(d)}>

            <rect
              rx={2}
              ry={2}
              width={22}
              height={22}
              stroke={'#373737'}
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={scale(getValue(d))}
            />

            <g transform="translate(11,15)"
               textAnchor="middle"
               pointerEvents="none"
               fill={'#fff'}>

              {shouldStrokeText(getValue(d)) && (
                <text
                  fill={'#fff'}
                  stroke={'#fff'}
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fontFamily="arial,sans-serif"
                  fontSize={10}
                  fontWeight={600}
                  children={d.a}
                />
              )}

              <text
                fill={textFill(getValue(d))}
                stroke="none"
                fontFamily="arial,sans-serif"
                pointerEvents="none"
                fontSize={10}
                fontWeight={600}
                children={d.a}
              />

            </g>

          </g>
        ))}
      </g>
    )
  }
}
